import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import { IIndividualPosition } from '../../interfaces';
import { getPositionTranslationKey } from '../../utils/getPositionTranslationKey.ts';
import { useTranslation } from 'react-i18next';

interface AleaIndividualPositionsProps {
  positions: IIndividualPosition[];
  setPositions: (positions: IIndividualPosition[]) => void;
}

const AleaIndividualPositions: React.FC<AleaIndividualPositionsProps> = ({
  positions,
  setPositions,
}) => {
  const {t} = useTranslation();
  const [selectedPositionIndex, setSelectedPositionIndex] = useState<
    number | null
  >(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const handleClick = (index: number) => {
    if (selectedPositionIndex === index) {
      return;
    }
    setSelectedPositionIndex(index);
  };

  const handleMaxHoursChange = (index: number, value: number) => {
    const updatedPositions = positions.map((pos, i) =>
      i === index ? { ...pos, maxHours: value } : pos
    );
    setPositions(updatedPositions);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setSelectedPositionIndex(null);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <Flex ref={containerRef} flexFlow="wrap">
      {positions.map((position, index) => (
        <Box
          key={index}
          onClick={() => handleClick(index)}
          border="1px solid"
          borderColor={
            selectedPositionIndex === index ? 'alea.500' : 'gray.300'
          }
          h="50px"
          borderRadius="20px"
          mr="10px"
          mb="10px"
          p="15px"
          display="flex"
          alignItems="center"
          position="relative"
          cursor="pointer"
        >
          {selectedPositionIndex === index ? (
            <Flex alignItems="center" fontWeight="500">
              <Text mr="2">{t(getPositionTranslationKey(position.name))}</Text>
              <Flex
                h="50px"
                borderLeft="1px solid"
                borderColor="alea.500"
                alignItems="center"
                pl="15px"
                pr="10px"
              >
                <Text fontWeight={400}>max.</Text>
              </Flex>
              <NumberInput
                width="60px"
                value={position.maxHours ?? 0} // Default value is 0
                onChange={(valueString) =>
                  handleMaxHoursChange(index, parseInt(valueString, 10))
                }
                mr="2"
                min={0}
                max={5}
                onClick={(e) => e.stopPropagation()} // Prevent closing on number input click
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontWeight={400}>h</Text>
            </Flex>
          ) : (
            <Text
              fontWeight={
                position.maxHours !== undefined && position.maxHours > 0
                  ? '500'
                  : 'normal'
              }
            >
              {t(getPositionTranslationKey(position.name))}{' '}
              {position.maxHours !== undefined &&
                position.maxHours > 0 &&
                `max. ${position.maxHours} h`}
            </Text>
          )}
        </Box>
      ))}
    </Flex>
  );
};

export default AleaIndividualPositions;
